import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
// Auth pages are kept eager — they are tiny and required immediately on load.
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import ChangePassword from './pages/Auth/ChangePassword';
import Unauthorized from './pages/Auth/Unauthorized';
import ProtectedRoute from './routes/ProtectedRoute';
import ProductAccessRoute from './routes/ProductAccessRoute';
import ToastContainer from './components/ui/ToastContainer';
import { ProductProvider, useProductContext } from './context/ProductContext';
import { clearAuthStorage } from './utils/auth';
import './App.css';

const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const MainMenu = lazy(() => import('./pages/MainMenu/MainMenu'));
const UserManagement = lazy(() => import('./pages/Users/UserManagement'));
const ProductManagementPage = lazy(() => import('./pages/Products/ProductManagement'));
const YoutubeDashboard = lazy(() => import('./pages/Youtube/YoutubeDashboard'));
const AppHealthPage = lazy(() => import('./pages/AppHealth/AppHealthPage'));
const ChooseProduct = lazy(() => import('./pages/ProductSelection/ChooseProduct'));
const GoogleAdsPage = lazy(() => import('./pages/Google/GoogleAdsPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#718096', fontSize: '0.9rem' }}>
    Loading…
  </div>
);

// Wrapper so YoutubeDashboard receives the context-selected product id
// Falls back to ThinkRight if no product is currently selected (admin & user alike)
const YoutubeRoute = () => {
  const { selectedProductId, products } = useProductContext();

  const effectiveProductId = React.useMemo(() => {
    if (selectedProductId) return selectedProductId;
    const thinkRight = products.find((p) =>
      p.name.toLowerCase().replace(/\s+/g, '').includes('thinkright')
    );
    return thinkRight ? thinkRight.id : '';
  }, [selectedProductId, products]);

  return <YoutubeDashboard selectedProduct={effectiveProductId} />;
};

const AppRoutes = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleLogoutRequired = (event) => {
      clearAuthStorage();

      if (event.detail?.message) {
        window.showToast(event.detail.message, 'warning');
      }

      navigate('/login', { replace: true });
    };

    const handlePasswordChangeRequired = () => {
      navigate('/change-password', { replace: true });
    };

    window.addEventListener('auth:logout-required', handleLogoutRequired);
    window.addEventListener('auth:password-change-required', handlePasswordChangeRequired);

    return () => {
      window.removeEventListener('auth:logout-required', handleLogoutRequired);
      window.removeEventListener('auth:password-change-required', handlePasswordChangeRequired);
    };
  }, [navigate]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        <Route
          path="/change-password"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'PRODUCT_USER']}>
              <ChangePassword />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'PRODUCT_USER']}>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/main-menu"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'PRODUCT_USER']}>
              <ProductAccessRoute>
                <MainMenu />
              </ProductAccessRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-panel"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/products"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ProductManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/choose-product"
          element={
            <ProtectedRoute allowedRoles={['PRODUCT_USER']}>
              <ChooseProduct />
            </ProtectedRoute>
          }
        />

        <Route
          path="/youtube"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <YoutubeRoute />
            </ProtectedRoute>
          }
        />


        <Route
          path="/app-health"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'PRODUCT_USER']}>
              <AppHealthPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/google-ads"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <GoogleAdsPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <Router>
      <div className="App">
        <ToastContainer />
        <ProductProvider>
          <AppRoutes />
        </ProductProvider>
      </div>
    </Router>
  );
}

export default App;
